import { cloneElement, type ReactElement, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  useHasProjectPermission,
  useHasWorkspacePermission,
  useIsProjectPermissionsResolving,
  useIsWorkspacePermissionsResolving,
} from '@/entities/user';
import { getNoPermissionMessage, PermissionTooltip } from './PermissionTooltip';

/** Which resolved permission scope the gate checks against. */
type PermissionScopeKind = 'project' | 'workspace';

interface ScopedPermissionProps {
  /**
   * Any-of the granular permissions required to enable the child. Pass
   * `PROJECT_PERMISSIONS.*` (project scope) or a granular workspace permission
   * such as `PERMISSIONS.ProjectCreate`. The org/workspace-admin override is
   * folded in automatically, so omit this only for genuinely admin-only actions
   * (`scope="workspace"` alone), which the fold then gates by itself.
   */
  permissions?: readonly string[];
  /**
   * Which scope's resolved permission list to check. `project` (default) reads
   * the active project's list; `workspace` reads the active workspace's list —
   * for workspace-level actions like creating a project.
   */
  scope?: PermissionScopeKind;
  /**
   * The control to render when permitted. When the caller lacks permission and
   * no `fallback` is given, it is cloned with `disabled: true` — so for that
   * default path it must accept a `disabled` prop.
   */
  children: ReactElement;
  /**
   * What to render in place of `children` when the caller lacks permission.
   * Use for controls that don't accept a `disabled` prop (cloning wouldn't
   * disable them) or to show a distinct disabled representation. When omitted,
   * `children` is cloned with `disabled: true`.
   */
  fallback?: ReactNode;
  /** Optional override for the tooltip copy. */
  message?: string;
  /**
   * Extra classes for layout that must hold regardless of permission — e.g.
   * `flex-1` to keep a control's share of a flex row. When permitted, merged
   * onto `children`. When disabled, merged onto both the wrapping tooltip span
   * (so the column keeps its size) and `children` (so the control fills it).
   */
  className?: string;
}

type GateProps = Omit<ScopedPermissionProps, 'scope'>;

function ProjectGate({ permissions, ...rest }: GateProps) {
  return renderGate(useHasProjectPermission(permissions), useIsProjectPermissionsResolving(), rest);
}

function WorkspaceGate({ permissions, ...rest }: GateProps) {
  return renderGate(
    useHasWorkspacePermission(permissions),
    useIsWorkspacePermissionsResolving(),
    rest,
  );
}

function renderGate(
  allowed: boolean,
  resolving: boolean,
  {
    children,
    fallback,
    message = getNoPermissionMessage(),
    className,
  }: {
    children: ReactElement;
    fallback?: ReactNode;
    message?: string;
    className?: string;
  },
): ReactNode {
  if (allowed) {
    if (!className) return children;
    const child = children as ReactElement<{ className?: string }>;
    return cloneElement(child, { className: clsx(child.props.className, className) });
  }

  const disabledContent =
    fallback ??
    cloneElement(children as ReactElement<{ disabled?: boolean; className?: string }>, {
      disabled: true,
      className: clsx(
        (children as ReactElement<{ className?: string }>).props.className,
        className,
      ),
    });

  // Still resolving: we don't yet know if the caller is permitted, so suppress
  // the definitive no-permission tooltip and show a loading cursor instead.
  if (resolving) {
    return <span className={clsx('flex w-full cursor-wait', className)}>{disabledContent}</span>;
  }

  return (
    <PermissionTooltip disabled message={message} className={className}>
      {disabledContent}
    </PermissionTooltip>
  );
}

/**
 * Declarative gate for a single action control against a resolved scoped
 * permission list. Reads the permissions itself (via `useHasProjectPermission`
 * / `useHasWorkspacePermission`) so call sites only declare *what* they require,
 * at *which* scope, and *what* to render:
 *
 * ```tsx
 * <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
 *   <Button onClick={onEdit}>Edit</Button>
 * </ScopedPermission>
 *
 * <ScopedPermission scope="workspace" permissions={[PERMISSIONS.ProjectCreate]}>
 *   <Button onClick={onNewProject}>New project</Button>
 * </ScopedPermission>
 * ```
 */
export function ScopedPermission({ scope = 'project', ...rest }: ScopedPermissionProps) {
  return scope === 'workspace' ? <WorkspaceGate {...rest} /> : <ProjectGate {...rest} />;
}
